import { SignInIdentifier, type VerificationCodeIdentifier } from '@logto/schemas';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTimer } from 'react-timer-hook';

import CaptchaContext from '@/Providers/CaptchaContextProvider/CaptchaContext';
import { identifyWithVerificationCode, resetPassword } from '@/apis/experience';
import { sendVerificationCodeApi } from '@/apis/utils';
import { isValidEvaPhoneNumber } from '@/components/eva/EvaPhoneCodeSignIn/phone-validation';
import useApi from '@/hooks/use-api';
import useErrorHandler from '@/hooks/use-error-handler';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import usePasswordPolicyChecker from '@/hooks/use-password-policy-checker';
import usePasswordRejectionErrorHandler from '@/hooks/use-password-rejection-handler';
import { useForgotPasswordSettings } from '@/hooks/use-sie';
import useToast from '@/hooks/use-toast';
import { UserFlow } from '@/types';
import { sendWithSmsBypass } from '@/utils/sms-bypass';

import {
  bindField,
  buildMethodTabs,
  buildPasswordLiveSetters,
  buildSendCodeErrorHandlers,
  getAvailableResetMethods,
  getErrorCode,
  getTimerExpiry,
} from './utils';

export type ResetMethod = 'phone' | 'email';

const phoneCountryCallingCode = '86';
const verificationCodeLength = 6;
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * EVA 单页重置密码的全部状态与逻辑(渲染见同目录 index.tsx)。内部仍走上游
 * forgot-password 交互:发码 → 验码并 identify(ForgotPassword 事件此时固定回
 * `user.new_password_required_in_profile`,这是「验码通过、待设新密码」的信号,
 * 不是失败)→ PUT password + submit。密码规则只在服务端(Logto)一处校验,
 * 前端只查非空与两次一致。
 */
const useEvaForgotPassword = () => {
  const { t } = useTranslation();
  const { enabledMethodSet } = useForgotPasswordSettings();
  const availableMethods = useMemo(
    () => getAvailableResetMethods(enabledMethodSet),
    [enabledMethodSet]
  );

  const [method, setMethod] = useState<ResetMethod>(availableMethods[0] ?? 'phone');
  const [identifier, setIdentifier] = useState('');
  const [code, setCode] = useState('');
  const [verificationId, setVerificationId] = useState<string>();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [identifierError, setIdentifierError] = useState('');
  const [codeError, setCodeError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmError, setConfirmError] = useState('');
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { executeCaptcha } = useContext(CaptchaContext);
  const handleError = useErrorHandler();
  const navigate = useNavigateWithPreservedSearchParams();
  const { setToast } = useToast();
  const sendVerificationCode = useApi(sendVerificationCodeApi);
  const identifyWithCode = useApi(identifyWithVerificationCode);
  const asyncResetPassword = useApi(resetPassword);
  const checkPassword = usePasswordPolicyChecker({
    setErrorMessage: (message) => {
      setPasswordError(message ?? '');
    },
  });
  const passwordRejectionErrorHandler = usePasswordRejectionErrorHandler({
    setErrorMessage: (message) => {
      setPasswordError(message ?? '');
    },
  });

  const { seconds, isRunning, restart } = useTimer({
    autoStart: false,
    expiryTimestamp: getTimerExpiry(),
  });

  const isEmail = method === 'email';
  const identifierValue = isEmail ? identifier.trim() : `${phoneCountryCallingCode}${identifier}`;

  const verificationIdentifier = useMemo<VerificationCodeIdentifier>(
    () => ({
      type: isEmail ? SignInIdentifier.Email : SignInIdentifier.Phone,
      value: identifierValue,
    }),
    [identifierValue, isEmail]
  );

  const switchMethod = useCallback((next: ResetMethod) => {
    setMethod(next);
    setIdentifier('');
    setCode('');
    setVerificationId(undefined);
    setIdentifierError('');
    setCodeError('');
  }, []);

  const invalidIdentifierMessage = isEmail
    ? t('eva_domestic.invalid_email')
    : t('eva_domestic.invalid_phone_length');

  const validateIdentifier = useCallback(() => {
    const valid = isEmail ? emailPattern.test(identifierValue) : isValidEvaPhoneNumber(identifier);

    if (!valid) {
      setIdentifierError(invalidIdentifierMessage);
    }

    return valid;
  }, [identifier, identifierValue, isEmail, invalidIdentifierMessage]);

  const handleSendCode = useCallback(async () => {
    setIdentifierError('');
    setCodeError('');

    if (!validateIdentifier()) {
      return;
    }

    setIsSendingCode(true);

    try {
      const [error, result] = await sendWithSmsBypass(executeCaptcha, async (captchaToken) =>
        sendVerificationCode(
          UserFlow.ForgotPassword,
          verificationIdentifier,
          undefined,
          captchaToken
        )
      );

      if (error) {
        await handleError(
          error,
          buildSendCodeErrorHandlers({
            t,
            invalidIdentifierMessage,
            setIdentifierError,
            setCodeError,
            restartCooldown: (seconds) => {
              const expiry = new Date();
              expiry.setSeconds(expiry.getSeconds() + seconds);
              restart(expiry, true);
            },
          })
        );

        return;
      }

      if (result) {
        setVerificationId(result.verificationId);
        restart(getTimerExpiry(), true);
      }
    } finally {
      setIsSendingCode(false);
    }
  }, [
    executeCaptcha,
    handleError,
    restart,
    sendVerificationCode,
    validateIdentifier,
    verificationIdentifier,
    invalidIdentifierMessage,
    t,
  ]);

  /** 表单逐项校验;返回 false 时已把原因写到对应字段的错误态 */
  const validateForm = useCallback(async () => {
    if (!validateIdentifier()) {
      return false;
    }
    if (!verificationId) {
      setCodeError(t('eva_domestic.request_code_first'));
      return false;
    }
    if (code.length !== verificationCodeLength) {
      setCodeError(t('eva_domestic.invalid_reset_code'));
      return false;
    }
    if (!password) {
      setPasswordError(t('eva_domestic.empty_password'));
      return false;
    }
    if (password !== confirmPassword) {
      setConfirmError(t('eva_domestic.password_mismatch'));
      return false;
    }

    return checkPassword(password);
  }, [checkPassword, code, confirmPassword, password, validateIdentifier, verificationId, t]);

  /** 验码 + identify;返回 true 表示可以继续设密码 */
  const identifyForReset = useCallback(async () => {
    const [identifyError, identifyResult] = await identifyWithCode({
      code,
      identifier: verificationIdentifier,
      verificationId: verificationId ?? '',
    });

    if (identifyResult) {
      navigate(`/${UserFlow.SignIn}`, { replace: true });
      return false;
    }

    if (identifyError) {
      const errorCode = await getErrorCode(identifyError);

      // ForgotPassword 事件验码通过后固定回这个码:表示「已 identify,待设新密码」
      if (errorCode === 'user.new_password_required_in_profile') {
        return true;
      }

      await handleError(identifyError, {
        'user.user_not_exist': () => {
          setIdentifierError(t('eva_domestic.account_not_registered'));
        },
        global: (error) => {
          setCodeError(error.message);
        },
      });
    }

    return false;
  }, [code, handleError, identifyWithCode, navigate, verificationId, verificationIdentifier, t]);

  const resetWithPassword = useCallback(async () => {
    const [resetError] = await asyncResetPassword(password);

    if (resetError) {
      await handleError(resetError, {
        'user.same_password': (error) => {
          setPasswordError(error.message);
        },
        'session.verification_session_not_found': () => {
          setCodeError(t('eva_domestic.verification_expired'));
        },
        ...passwordRejectionErrorHandler,
        global: (error) => {
          setPasswordError(error.message);
        },
      });

      return;
    }

    setToast(t('eva_domestic.password_updated'));
    navigate(`/${UserFlow.SignIn}`, { replace: true });
  }, [
    asyncResetPassword,
    handleError,
    navigate,
    password,
    passwordRejectionErrorHandler,
    setToast,
    t,
  ]);

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setIdentifierError('');
      setCodeError('');
      setPasswordError('');
      setConfirmError('');

      if (!(await validateForm())) {
        return;
      }

      setIsSubmitting(true);

      try {
        if (await identifyForReset()) {
          await resetWithPassword();
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [identifyForReset, resetWithPassword, validateForm]
  );

  const isIdentifierValid = isEmail
    ? emailPattern.test(identifierValue)
    : isValidEvaPhoneNumber(identifier);

  // 两次输入一致性实时校验(与个人中心「设置密码」弹窗同规)
  const { setPasswordLive, setConfirmPasswordLive } = buildPasswordLiveSetters({
    t,
    password,
    confirmPassword,
    setPassword,
    setPasswordError,
    setConfirmPassword,
    setConfirmError,
  });

  return {
    method,
    methodTabs: buildMethodTabs(availableMethods, t),
    switchMethod,
    isEmail,
    identifier,
    setIdentifier: bindField(setIdentifier, () => {
      setIdentifierError('');
    }),
    code,
    setCode: bindField(setCode, () => {
      setCodeError('');
    }),
    password,
    setPassword: setPasswordLive,
    confirmPassword,
    setConfirmPassword: setConfirmPasswordLive,
    identifierError,
    codeError,
    passwordError,
    confirmError,
    isSendingCode,
    isSubmitting,
    countdownSeconds: seconds,
    isCountingDown: isRunning,
    hasSentCode: Boolean(verificationId),
    isSendCodeDisabled: isSendingCode || isRunning || !isIdentifierValid,
    isSubmitDisabled:
      isSubmitting ||
      !isIdentifierValid ||
      code.length !== verificationCodeLength ||
      !password ||
      !confirmPassword ||
      password !== confirmPassword,
    handleSendCode,
    handleSubmit,
    goToSignIn: () => {
      navigate(`/${UserFlow.SignIn}`);
    },
  };
};

export default useEvaForgotPassword;
